import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NHCAppHeader, NHCLogo, T } from '@design/index';
import type { Role } from '@design/index';
import PatientTabBar from './PatientTabBar';

const TAB_ROUTE_MAP: Record<string, string> = {
  home: '/patient/home',
  records: '/patient/records',
  chat: '/patient/my-chat',
  neoai: '/patient/neoai',
  profile: '/patient/profile',
};

const ROUTE_TAB_MAP: Record<string, string> = {
  // Home & Care Discovery/Booking
  '/patient': 'home',
  '/patient/home': 'home',
  '/patient/care-services': 'home',
  '/patient/scan': 'home',
  '/patient/search': 'home',
  '/patient/marketplace': 'home',
  '/patient/marketplace/providers': 'home',
  '/patient/book-doctor': 'home',
  '/patient/book-lab': 'home',
  '/patient/labs': 'home',
  '/patient/lab': 'home',
  '/patient/lab-appointments': 'home',
  '/patient/imaging-discovery': 'home',
  '/patient/imaging-booking': 'home',
  '/patient/pharmacy': 'home',
  '/patient/emergency': 'home',
  '/patient/ambulance': 'home',
  // Records
  '/patient/records': 'records',
  '/patient/health-vault': 'records',
  '/patient/certificates': 'records',
  '/patient/vaccinations': 'records',
  '/patient/claims': 'records',
  '/patient/blood-bank': 'records',
  '/patient/share-center': 'records',
  '/patient/emergency-records': 'records',
  '/patient/lab-reports': 'records',
  '/patient/lab-invoices': 'records',
  '/patient/lab-payments': 'records',
  '/patient/timeline': 'records',
  '/patient/documents': 'records',
  '/patient/prescriptions': 'records',
  '/patient/diagnostics': 'records',
  '/patient/imaging': 'records',
  '/patient/access-logs': 'records',
  '/patient/access-log': 'records',
  // Chat
  '/patient/my-chat': 'chat',
  '/patient/chat': 'chat',
  '/patient/communication': 'chat',
  '/patient/telemedicine': 'chat',
  '/patient/calls': 'chat',
  '/patient/groups': 'chat',
  // NeoAI
  '/patient/neoai': 'neoai',
  // Profile & Account
  '/patient/profile': 'profile',
  '/patient/settings': 'profile',
  '/patient/change-password': 'profile',
  '/patient/nhc-card': 'profile',
  '/patient/family': 'profile',
  '/patient/insurance': 'profile',
  '/patient/consent': 'profile',
  '/patient/notifications': 'profile',
  '/patient/security': 'profile',
  '/patient/medical-basics': 'profile',
  '/patient/medicines': 'profile',
  '/patient/follow-ups': 'profile',
  '/patient/appointments': 'profile',
  '/patient/ecosystem-links': 'profile',
  '/patient/profile/registration': 'profile',
  '/patient/profile/medical-basics': 'profile',
  '/patient/profile/security': 'profile',
  '/patient/profile/app-lock': 'profile',
  '/patient/profile/legal': 'profile',
};

function useIsMobile(breakpoint = 1024) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning,';
  if (h < 17) return 'Good afternoon,';
  return 'Good evening,';
}

function PatientHomeHeader({ userName, nhcId, onNotification, showNotificationDot = false }: { userName: string; nhcId?: string; onNotification: () => void; showNotificationDot?: boolean }) {
  return (
    <div style={{ padding: '12px 16px 8px', background: T.white, borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <div style={{
            width: 42, height: 42, borderRadius: '50%',
            background: `linear-gradient(135deg, ${T.teal}20, ${T.navy}15)`,
            border: `2px solid ${T.teal}40`,
            display: 'grid', placeItems: 'center',
          }}>
            <span style={{ fontSize: 18, fontWeight: 800, color: T.teal, fontFamily: T.font }}>{userName?.charAt(0) || 'P'}</span>
          </div>
          <div style={{
            position: 'absolute', bottom: -1, right: -1,
            width: 18, height: 18, borderRadius: '50%',
            background: '#fff', border: `1.5px solid ${T.teal}40`,
            display: 'grid', placeItems: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.10)',
          }}>
            <NHCLogo w={11} variant="color" />
          </div>
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 400, color: '#64748B', fontFamily: T.font }}>{getGreeting()}</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#002B5C', fontFamily: T.font, lineHeight: 1.2 }}>{userName}</div>
          {nhcId && (
            <div style={{ fontSize: 11, fontWeight: 600, color: T.teal, fontFamily: "'SF Mono','Fira Code',ui-monospace,monospace", marginTop: 1, letterSpacing: '0.03em' }}>{nhcId}</div>
          )}
        </div>
      </div>
      <div onClick={onNotification} style={{ position: 'relative', cursor: 'pointer' }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.navy} strokeWidth="2" strokeLinecap="round"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 01-3.46 0" /></svg>
        {/* Single unread indicator — only when there are real unread notifications. */}
        {showNotificationDot && (
          <div style={{ position: 'absolute', top: -2, right: -2, width: 8, height: 8, borderRadius: 4, background: '#EF4444', border: '1.5px solid #FFF' }} />
        )}
      </div>
    </div>
  );
}

const HOME_PREFIXES = [
  '/patient/care-services/', '/patient/marketplace/', '/patient/labs/', '/patient/lab-booking/',
  '/patient/lab-profile/', '/patient/imaging-booking/', '/patient/imaging-discovery/',
  '/patient/provider/', '/patient/book-',
];
const PROFILE_PREFIXES = ['/patient/profile/', '/patient/legal/'];

function resolveTabByPrefix(path: string): string {
  if (HOME_PREFIXES.some(p => path.startsWith(p))) return 'home';
  if (PROFILE_PREFIXES.some(p => path.startsWith(p))) return 'profile';
  return 'home';
}

interface Props {
  children: React.ReactNode;
  role?: Role;
  userName?: string;
  nhcId?: string;
  showNotificationDot?: boolean;
}

export default function PatientSystemDesignShell({ children, role = 'patient', userName = 'Patient', nhcId, showNotificationDot = false }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const activeTab = ROUTE_TAB_MAP[location.pathname] || resolveTabByPrefix(location.pathname);
  const isSubPage = location.pathname !== '/patient' && location.pathname !== '/patient/home';

  const pageTitle = getPageTitle(location.pathname);

  function handleTabChange(tab: string) {
    const route = TAB_ROUTE_MAP[tab];
    if (route) navigate(route);
  }

  function handleBack() {
    navigate(-1);
  }

  function handleNotification() {
    navigate('/patient/notifications');
  }

  if (!isMobile) return <>{children}</>;

  const isHome = location.pathname === '/patient' || location.pathname === '/patient/home' || location.pathname === '/patient/profile';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        background: T.bg,
        fontFamily: T.font,
      }}
    >
      {isHome ? (
        <PatientHomeHeader userName={userName} nhcId={nhcId} onNotification={handleNotification} showNotificationDot={showNotificationDot} />
      ) : (
        <NHCAppHeader
          role={role}
          title={pageTitle}
          showBack={isSubPage}
          onBack={handleBack}
          showSearch={!isSubPage}
          onNotification={handleNotification}
        />
      )}

      <main
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: 70,
        }}
      >
        {children}
      </main>

      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          background: T.white,
        }}
      >
        {/* Patient-owned tab bar — approved order Home|Records|Chat|NeoAI|Profile
            (no Appts; appointments stay reachable via care flows). */}
        <PatientTabBar
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </div>
    </div>
  );
}

function getPageTitle(pathname: string): string {
  const titles: Record<string, string> = {
    '/patient': 'NeoHealthCard',
    '/patient/home': 'NeoHealthCard',
    '/patient/nhc-card': 'Digital NeoHealthCard',
    '/patient/scan': 'Scan QR / Barcode',
    '/patient/care-services': 'Care Services',
    '/patient/records': 'Records',
    '/patient/timeline': 'Health Timeline',
    '/patient/emergency': 'Emergency',
    '/patient/consent': 'Consent & Sharing',
    '/patient/access-logs': 'Access History',
    '/patient/medicines': 'Medicines',
    '/patient/insurance': 'Insurance',
    '/patient/family': 'Family Profiles',
    '/patient/lab': 'Lab Tracking',
    '/patient/pharmacy': 'Pharmacy',
    '/patient/follow-ups': 'Appointments',
    '/patient/profile': 'Profile',
    '/patient/settings': 'Edit Profile',
    '/patient/certificates': 'Certificates',
    '/patient/health-vault': 'Records',
    '/patient/share-center': 'Share Records',
    '/patient/neoai': 'NeoAI',
    '/patient/my-chat': 'Chat',
    '/patient/vaccinations': 'Vaccinations',
    '/patient/blood-bank': 'Blood Bank',
    '/patient/ecosystem-links': 'Ecosystem',
    '/patient/claims': 'Claims',
    '/patient/emergency-records': 'Emergency Records',
    '/patient/marketplace': 'Care Services',
    '/patient/search': 'Search',
    '/patient/labs': 'Find Labs',
    '/patient/lab-appointments': 'Lab Appointments',
    '/patient/lab-reports': 'Lab Reports',
    '/patient/lab-invoices': 'Lab Invoices',
    '/patient/lab-payments': 'Lab Payments',
    '/patient/change-password': 'Change Password',
    '/patient/notifications': 'Notifications',
    '/patient/security': 'Security',
    '/patient/medical-basics': 'Medical Basics',
    '/patient/communication': 'Chat',
    '/patient/telemedicine': 'Telemedicine',
    '/patient/prescriptions': 'Prescriptions',
    '/patient/diagnostics': 'Diagnostics & Imaging',
    '/patient/imaging': 'Diagnostics & Imaging',
    '/patient/documents': 'Hospital Documents',
    '/patient/appointments': 'My Appointments',
    '/patient/profile/registration': 'Registration & Verification',
    '/patient/profile/medical-basics': 'Medical Basics',
    '/patient/profile/security': 'Security',
    '/patient/access-log': 'Access History',
    '/patient/ambulance': 'Emergency',
    '/patient/profile/app-lock': 'App Lock',
    '/patient/profile/legal': 'Legal & Health Safety',
    '/patient/legal/terms': 'Terms of Service',
    '/patient/legal/privacy': 'Privacy Policy',
    '/patient/legal/medical-disclaimer': 'Medical Disclaimer',
    '/patient/legal/consent-policy': 'Consent Policy',
    '/patient/legal/emergency-access': 'Emergency Access Policy',
    '/patient/legal/ai-safety': 'AI Safety & Usage',
    '/patient/legal/telemedicine': 'Telemedicine Policy',
    '/patient/legal/data-retention': 'Data Retention',
    '/patient/legal/company': 'About NeoHealthCard',
    '/patient/book-doctor': 'Book Appointment',
    '/patient/book-lab': 'Book Lab Test',
    '/patient/imaging-discovery': 'Imaging & Diagnostics',
    '/patient/marketplace/providers': 'Find Providers',
    '/patient/care-services/doctors': 'Doctors',
    '/patient/care-services/labs': 'Labs',
    '/patient/care-services/pharmacy': 'Pharmacy',
    '/patient/care-services/imaging': 'Diagnostics & Imaging',
    '/patient/care-services/hospitals': 'Hospitals & OPD',
    '/patient/care-services/telemedicine': 'Telemedicine',
    '/patient/care-services/ambulance': 'Ambulance',
    '/patient/care-services/insurance': 'Insurance & Claims',
  };
  if (titles[pathname]) return titles[pathname];
  if (pathname.startsWith('/patient/care-services/doctors/') && pathname.endsWith('/book')) return 'Book Doctor';
  if (pathname.startsWith('/patient/care-services/doctors/')) return 'Doctor Profile';
  if (pathname.startsWith('/patient/care-services/labs/') && pathname.endsWith('/book')) return 'Book Lab Test';
  if (pathname.startsWith('/patient/care-services/labs/')) return 'Lab Store';
  if (pathname.startsWith('/patient/care-services/pharmacy/') && pathname.endsWith('/order')) return 'Order Medicines';
  if (pathname.startsWith('/patient/care-services/pharmacy/')) return 'Pharmacy Store';
  if (pathname.startsWith('/patient/care-services/imaging/')) return 'Imaging Center';
  if (pathname.startsWith('/patient/care-services/hospitals/') && pathname.endsWith('/book')) return 'Book OPD';
  if (pathname.startsWith('/patient/care-services/hospitals/')) return 'Hospital Profile';
  if (pathname.startsWith('/patient/labs/')) return 'Lab Profile';
  if (pathname.startsWith('/patient/lab-booking/')) return 'Book Lab Test';
  if (pathname.startsWith('/patient/imaging/')) return 'Imaging Provider';
  if (pathname.startsWith('/patient/marketplace/')) return 'Find Providers';
  return 'NeoHealthCard';
}
